import { Step } from 'react-joyride';

/**
 * Tour step definitions for the KELZIX guided walkthrough.
 * Steps are role-aware: common steps for all users, plus admin/super_admin extensions.
 */

// === Common steps (all authenticated users) ===
export const commonSteps: Step[] = [
  {
    target: '[data-tour="sidebar-nav"]',
    content: 'This is your navigation sidebar. Access all features from here — Dashboard, Alerts, Logbook, and more.',
    placement: 'right',
    disableBeacon: true,
    title: 'Navigation',
  },
  {
    target: '[data-tour="dashboard-nav"]',
    content: 'The Dashboard gives you a real-time overview of all your cold storage rooms and sensor readings.',
    placement: 'right',
    title: 'Dashboard',
  },
  {
    target: '[data-tour="kpi-stats"]',
    content: 'Key metrics at a glance — total rooms, sensors, online status, warnings, critical alerts, and out-of-range readings.',
    placement: 'bottom',
    title: 'KPI Statistics',
  },
  {
    target: '[data-tour="dashboard-controls"]',
    content: 'Search sensors, filter by status (online, warning, critical), sort by different criteria, and switch between card and table views.',
    placement: 'bottom',
    title: 'Search & Filters',
  },
  {
    target: '[data-tour="room-cards"]',
    content: 'Each card represents a cold storage room. Click any room to see detailed sensor readings, charts, and compliance data.',
    placement: 'top',
    title: 'Room Cards',
  },
  {
    target: '[data-tour="notification-bell"]',
    content: 'Real-time notifications appear here. You\'ll see alerts for temperature excursions, sensor disconnections, and other events.',
    placement: 'bottom',
    title: 'Notifications',
  },
  {
    target: '[data-tour="alerts-nav"]',
    content: 'The Alerts page shows all active temperature and humidity alerts. You can acknowledge alerts and add corrective actions.',
    placement: 'right',
    title: 'Alerts',
  },
  {
    target: '[data-tour="logbook-nav"]',
    content: 'The Logbook tracks pharmaceutical product storage — batch management, storage entries, and compliance documentation.',
    placement: 'right',
    title: 'Storage Logbook',
  },
  {
    target: '[data-tour="settings-nav"]',
    content: 'Configure your alert preferences, notification settings, and profile from the Settings page.',
    placement: 'right',
    title: 'Settings',
  },
];

// === Admin extension steps (admin, manager roles) ===
export const adminSteps: Step[] = [
  {
    target: '[data-tour="analytics-nav"]',
    content: 'Analytics provides temperature trends, room comparisons, compliance metrics, and WiFi signal quality charts over time.',
    placement: 'right',
    title: 'Analytics',
  },
  {
    target: '[data-tour="audit-nav"]',
    content: 'Generate compliance reports, view audit trails, manage gap declarations, and export PDF reports for regulatory audits.',
    placement: 'right',
    title: 'Audit & Reports',
  },
  {
    target: '[data-tour="users-nav"]',
    content: 'Manage users — invite team members, assign roles (admin, manager, supervisor, viewer), and control access.',
    placement: 'right',
    title: 'User Management',
  },
];

// === Super admin extension steps ===
export const superAdminSteps: Step[] = [
  {
    target: '[data-tour="tenants-nav"]',
    content: 'Manage all tenants (organizations) in the system. Create new tenants, set plans, resource limits, and certificate prefixes.',
    placement: 'right',
    title: 'Tenant Management',
  },
  {
    target: '[data-tour="rooms-nav"]',
    content: 'Create and configure cold storage rooms with temperature and humidity thresholds for compliance monitoring.',
    placement: 'right',
    title: 'Room Management',
  },
  {
    target: '[data-tour="sensors-nav"]',
    content: 'Register and manage sensor nodes. Assign sensors to rooms, configure protocols (MQTT/HTTP), and monitor device status.',
    placement: 'right',
    title: 'Sensor Nodes',
  },
  {
    target: '[data-tour="devices-nav"]',
    content: 'Device management — generate activation codes, track fleet health, manage firmware versions, and device lifecycle events.',
    placement: 'right',
    title: 'Device Management',
  },
  {
    target: '[data-tour="tenant-switcher"]',
    content: 'Switch between tenants to view their data. As a super admin, you can impersonate any tenant\'s view.',
    placement: 'bottom',
    title: 'Tenant Switcher',
  },
];

/**
 * Build the tour steps array based on user role.
 */
export function getTourSteps(role: string): Step[] {
  const steps = [...commonSteps];

  // Admin roles get admin extension
  if (['super_admin', 'admin', 'manager'].includes(role)) {
    steps.push(...adminSteps);
  }

  // Super admin gets additional infrastructure steps
  if (role === 'super_admin') {
    steps.push(...superAdminSteps);
  }

  return steps;
}
